/**
 * 🆕 [v13.66 Volet B — 2026-02-28] Tableau de bord global des bonus (vue manager)
 * 🆕 [v13.67 — 2026-02-28] Ajout drill-down par employé (mes_bts) + KPI aligné sur v13.67
 *
 * Consomme `GET /api/bonus/dashboard-global` (guardé côté backend par la clé
 * RBAC `bonus_dashboard_global` — aucun rôle hardcodé).
 *
 * Affiche :
 *   - KPI en haut : Total à verser (v13.67) + pools (peintres v13.67 / chefs legacy / CP)
 *   - Tableau par employé (bonus + statut admissibilité + drill-down « Voir mes BT »)
 *   - Tableau par équipe (sous-totaux legacy — replié par défaut)
 *
 * Ref : /app/memory/RCA_V13_66_BONUS_ZERO.md § Volet B
 * Ref : /app/memory/RCA_V13_67_BONUS_PAR_BT.md
 */
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle, XCircle, Users, Wallet, Info, TrendingUp,
  ChevronDown, ChevronRight, ClipboardList, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { MesBonsTravailSaison } from "@/components/bonus/MesBonsTravailSaison";

const formatCAD = (n) =>
  new Intl.NumberFormat('fr-CA', {
    style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
  }).format(n || 0);

const formatH = (n) =>
  `${(Number(n) || 0) > 0 ? '+' : ''}${(Number(n) || 0).toFixed(2).replace('.', ',')} h`;

const formatHAbs = (n) => `${(Number(n) || 0).toFixed(2).replace('.', ',')} h`;

const PALIER_COLOR = {
  Bronze:  "bg-amber-100 text-amber-800 border-amber-300",
  Argent:  "bg-slate-100 text-slate-800 border-slate-300",
  Or:      "bg-yellow-100 text-yellow-800 border-yellow-400",
  Platine: "bg-blue-100 text-blue-800 border-blue-300",
  Aucun:   "bg-slate-50 text-slate-500 border-slate-200",
};

export const BonusDashboardGlobal = ({ data, loading, saison }) => {
  const totalAVerser = data?.total_a_verser ?? 0;
  const pool = data?.pool_totaux || { peintres: 0, chefs: 0, cp: 0 };
  const employes = useMemo(() => data?.employes || [], [data]);
  const equipes = useMemo(() => data?.equipes || [], [data]);
  const palierCp = data?.palier_cp || { niveau: "Aucun", pct: 0 };

  // 🆕 [v13.67] Drill-down par employé
  const [expandedEmpId, setExpandedEmpId] = useState(null);
  const [drillDownData, setDrillDownData] = useState({}); // { emp_id: { loading, mes_bts, error } }

  const handleToggleDrilldown = async (empId) => {
    if (expandedEmpId === empId) {
      setExpandedEmpId(null);
      return;
    }
    setExpandedEmpId(empId);
    if (!drillDownData[empId]) {
      setDrillDownData((prev) => ({ ...prev, [empId]: { loading: true } }));
      try {
        const res = await api.get(`/api/bonus/employe/${empId}`, {
          params: { saison: saison || undefined },
        });
        setDrillDownData((prev) => ({
          ...prev,
          [empId]: { loading: false, mes_bts: res.data?.mes_bts || [], bonus_estime: res.data?.bonus_estime },
        }));
      } catch (err) {
        setDrillDownData((prev) => ({
          ...prev,
          [empId]: { loading: false, error: err?.response?.data?.detail || "Erreur de chargement" },
        }));
      }
    }
  };

  if (loading) {
    return (
      <div
        className="text-center py-16 text-slate-500"
        data-testid="dashboard-global-loading"
      >
        Calcul du tableau de bord global en cours…
      </div>
    );
  }

  if (!data) {
    return (
      <Card data-testid="dashboard-global-empty">
        <CardContent className="pt-6 text-center text-slate-500">
          Aucune donnée à afficher pour la saison sélectionnée.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-global-root">
      {/* ── KPI Total à verser (v13.67) ── */}
      <Card
        className="border-2 border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50"
        data-testid="kpi-total-a-verser-card"
      >
        <CardContent className="pt-6 pb-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Wallet className="w-8 h-8 text-blue-700" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 flex items-center gap-1">
                  Total à verser — {saison || "—"}
                  <Badge variant="outline" className="text-[9px] bg-white/70 border-blue-300 text-blue-700">
                    Moteur v13.67 · par BT
                  </Badge>
                </div>
                <div
                  className="text-4xl sm:text-5xl font-bold text-blue-900 mt-0.5"
                  data-testid="kpi-total-a-verser-value"
                >
                  {formatCAD(totalAVerser)}
                </div>
                <div className="text-xs text-blue-700 mt-1">
                  Σ des bonus estimés (par employé, calculés par BT) + bonus CP — {data?.nb_employes ?? 0} employé(s)
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-6 text-sm">
              <div className="text-center" data-testid="kpi-pool-peintres">
                <div className="text-[11px] text-blue-600 font-medium uppercase tracking-wide">
                  Employés (v13.67)
                </div>
                <div className="text-2xl font-bold text-blue-800">
                  {formatCAD(pool.peintres)}
                </div>
                <div className="text-[9px] text-slate-500 mt-0.5">
                  Σ bonus par BT
                </div>
              </div>
              <div className="text-center" data-testid="kpi-pool-chefs">
                <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">
                  Pool chefs (legacy)
                </div>
                <div className="text-2xl font-bold text-slate-600">
                  {formatCAD(pool.chefs)}
                </div>
                <div className="text-[9px] text-slate-400 mt-0.5">
                  Vue équipe v13.66
                </div>
              </div>
              <div className="text-center" data-testid="kpi-pool-cp">
                <div className="text-[11px] text-blue-600 font-medium uppercase tracking-wide">
                  Chargée(s) de projet
                </div>
                <div className="text-2xl font-bold text-blue-800">
                  {formatCAD(pool.cp)}
                </div>
                {palierCp?.niveau && palierCp.niveau !== "Aucun" && (
                  <Badge
                    variant="outline"
                    className={`mt-1 text-[10px] ${PALIER_COLOR[palierCp.niveau] || PALIER_COLOR.Aucun}`}
                  >
                    Palier CP {palierCp.niveau}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Ventilation par employé (avec drill-down v13.67) ── */}
      <Card data-testid="table-employes-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-slate-600" />
            Ventilation par employé
            <Badge variant="outline" className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
              v13.67 · par BT
            </Badge>
          </CardTitle>
          <div className="text-[11px] text-slate-500 mt-1">
            Cliquez sur une ligne pour voir le détail par BT de l'employé.
          </div>
        </CardHeader>
        <CardContent>
          {employes.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              Aucun employé actif cette saison.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="table-employes"
              >
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b">
                    <th className="py-2 pr-1 w-6"></th>
                    <th className="py-2 pr-3">Employé</th>
                    <th className="py-2 pr-3">Rôle</th>
                    <th className="py-2 pr-3">Chef principal (legacy)</th>
                    <th className="py-2 pr-3 text-right">Heures saison</th>
                    <th className="py-2 pr-3 text-right">Éco perso (net)</th>
                    <th className="py-2 pr-3 text-right">Bonus estimé</th>
                    <th className="py-2 pr-3 text-center">Admissibilité</th>
                  </tr>
                </thead>
                <tbody>
                  {employes.map((e) => {
                    const isExpanded = expandedEmpId === e.employee_id;
                    const drill = drillDownData[e.employee_id];
                    return (
                      <>
                        <tr
                          key={e.employee_id}
                          className={`border-b last:border-b-0 hover:bg-slate-50 cursor-pointer ${isExpanded ? "bg-emerald-50/40" : ""}`}
                          data-testid={`row-employe-${e.employee_id}`}
                          onClick={() => handleToggleDrilldown(e.employee_id)}
                        >
                          <td className="py-2.5 pr-1 text-slate-500">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="py-2.5 pr-3 font-medium text-slate-800">
                            {e.employee_nom}
                          </td>
                          <td className="py-2.5 pr-3 text-slate-600">
                            {e.role}
                          </td>
                          <td className="py-2.5 pr-3 text-slate-600">
                            {e.chef_equipe_nom || "—"}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-slate-700">
                            {formatHAbs(e.heures_saison)}
                          </td>
                          <td
                            className={`py-2.5 pr-3 text-right font-semibold ${
                              (e.heures_economisees_perso || 0) > 0 ? "text-emerald-700"
                                : (e.heures_economisees_perso || 0) < 0 ? "text-red-600"
                                : "text-slate-500"
                            }`}
                          >
                            {formatH(e.heures_economisees_perso)}
                          </td>
                          <td className="py-2.5 pr-3 text-right font-bold text-blue-800">
                            {formatCAD(e.bonus_estime)}
                          </td>
                          <td className="py-2.5 pr-3 text-center">
                            {e.eligible ? (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                                <CheckCircle className="w-3 h-3 mr-1 inline" />
                                Admissible
                              </Badge>
                            ) : (
                              <span className="inline-flex items-center gap-1">
                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                                  <XCircle className="w-3 h-3 mr-1 inline" />
                                  Non admissible
                                </Badge>
                                {(e.raisons_ineligibilite?.length > 0 || e.bonus_zero_explication) && (
                                  <span
                                    className="text-slate-400 cursor-help"
                                    title={
                                      e.bonus_zero_explication?.details
                                        || (e.raisons_ineligibilite || []).join(" · ")
                                    }
                                    data-testid={`raison-${e.employee_id}`}
                                  >
                                    <Info className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr
                            className="bg-emerald-50/20"
                            data-testid={`drilldown-employe-${e.employee_id}`}
                          >
                            <td colSpan={8} className="py-3 px-3">
                              {drill?.loading && (
                                <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Chargement des BT de {e.employee_nom}…
                                </div>
                              )}
                              {drill?.error && (
                                <div className="text-sm text-red-600 py-2">
                                  ⚠️ {drill.error}
                                </div>
                              )}
                              {!drill?.loading && !drill?.error && Array.isArray(drill?.mes_bts) && drill.mes_bts.length === 0 && (
                                <div className="text-sm text-slate-500 italic py-4">
                                  Cet employé n'a pointé sur aucun BT durant cette saison.
                                </div>
                              )}
                              {!drill?.loading && !drill?.error && Array.isArray(drill?.mes_bts) && drill.mes_bts.length > 0 && (
                                <MesBonsTravailSaison mesBts={drill.mes_bts} />
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="py-2.5"></td>
                    <td className="py-2.5 pr-3 font-bold text-slate-800" colSpan={5}>
                      Total ({employes.length} employé{employes.length > 1 ? 's' : ''})
                    </td>
                    <td className="py-2.5 pr-3 text-right font-bold text-blue-900" data-testid="table-employes-total">
                      {formatCAD(employes.reduce((s, e) => s + (e.bonus_estime || 0), 0))}
                    </td>
                    <td className="py-2.5 pr-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Vue équipe (legacy v13.66) — repliée par défaut ── */}
      <details className="group" data-testid="equipes-legacy-details">
        <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-800 flex items-center gap-2 px-2 py-2 select-none bg-slate-100 rounded">
          <ChevronRight className="w-4 h-4 group-open:hidden" />
          <ChevronDown className="w-4 h-4 hidden group-open:inline" />
          <Users className="w-4 h-4 text-slate-500" />
          Voir la ventilation par équipe (vue historique v13.66 · à titre informatif)
          <Badge variant="outline" className="ml-1 text-[10px] bg-slate-50 text-slate-500 border-slate-300">
            {equipes.length} équipe{equipes.length > 1 ? "s" : ""}
          </Badge>
        </summary>

        <Card className="mt-3" data-testid="table-equipes-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-slate-700">
              <Users className="w-4 h-4 text-slate-500" />
              Sous-totaux par équipe (legacy v13.66)
            </CardTitle>
            <div className="text-[11px] text-slate-500 mt-1">
              ℹ️ Cette vue est conservée à titre informatif. Depuis la refonte v13.67, le calcul du bonus se fait par bon de travail. Les paliers d'équipe ne servent plus au calcul mais restent lisibles pour comparaison.
            </div>
          </CardHeader>
          <CardContent>
            {equipes.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">
                Aucune équipe avec activité cette saison.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid="table-equipes"
                >
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b">
                      <th className="py-2 pr-3">Chef d'équipe</th>
                      <th className="py-2 pr-3 text-right">Budget</th>
                      <th className="py-2 pr-3 text-right">Réel</th>
                      <th className="py-2 pr-3 text-right">Écart</th>
                      <th className="py-2 pr-3 text-center">Palier</th>
                      <th className="py-2 pr-3 text-right">Chef (legacy)</th>
                      <th className="py-2 pr-3 text-right">Peintres (legacy)</th>
                      <th className="py-2 pr-3 text-right">Sous-total legacy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipes.map((eq) => {
                      const niveau = eq?.palier?.niveau || "Aucun";
                      const cls = PALIER_COLOR[niveau] || PALIER_COLOR.Aucun;
                      const ecart = eq.heures_economisees ?? 0;
                      return (
                        <tr
                          key={eq.chef_equipe_id}
                          className="border-b last:border-b-0 hover:bg-slate-50"
                          data-testid={`row-equipe-${eq.chef_equipe_id}`}
                        >
                          <td className="py-2.5 pr-3 font-medium text-slate-800">
                            {eq.chef_equipe_nom}
                            <div className="text-[10px] text-slate-400">
                              {eq.nb_bons_travail} BT · {eq.nb_peintres} peintre(s)
                            </div>
                          </td>
                          <td className="py-2.5 pr-3 text-right text-slate-700">
                            {formatHAbs(eq.heures_budgetees)}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-slate-700">
                            {formatHAbs(eq.heures_reelles)}
                          </td>
                          <td
                            className={`py-2.5 pr-3 text-right font-semibold ${
                              ecart > 0 ? "text-emerald-700" : ecart < 0 ? "text-red-600" : "text-slate-500"
                            }`}
                          >
                            {formatH(ecart)}
                          </td>
                          <td className="py-2.5 pr-3 text-center">
                            <Badge variant="outline" className={`text-[10px] ${cls}`}>
                              {niveau}
                            </Badge>
                          </td>
                          <td className="py-2.5 pr-3 text-right text-slate-500">
                            {formatCAD(eq.bonus_chef_brut)}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-slate-500">
                            {formatCAD(eq.sous_total_peintres)}
                          </td>
                          <td className="py-2.5 pr-3 text-right font-semibold text-slate-600">
                            {formatCAD(eq.sous_total_equipe)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="py-2.5 pr-3 font-bold text-slate-800">
                        Total
                      </td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-slate-800">
                        {formatHAbs(equipes.reduce((s, e) => s + (e.heures_budgetees || 0), 0))}
                      </td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-slate-800">
                        {formatHAbs(equipes.reduce((s, e) => s + (e.heures_reelles || 0), 0))}
                      </td>
                      <td className="py-2.5 pr-3 text-right"></td>
                      <td className="py-2.5 pr-3"></td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-slate-600">
                        {formatCAD(pool.chefs)}
                      </td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-slate-600">
                        {formatCAD(pool.peintres_legacy_v13_66 ?? 0)}
                      </td>
                      <td className="py-2.5 pr-3 text-right font-bold text-slate-700">
                        {formatCAD(pool.chefs + (pool.peintres_legacy_v13_66 ?? 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </details>
    </div>
  );
};

export default BonusDashboardGlobal;
